import type { Dayjs } from 'dayjs';

import dayjs from '@didaskill/services/dayjs';

import type { Availability } from '../types';

export type UseAvailabilityParams = {
  accessStartDate: Date;
  availability?: Availability;
};

export type UseAvailabilityResult = {
  /**
   * `true` if the quest cannot yet be accessed, `false` otherwise.
   */
  isLocked: boolean;
  /**
   * How many days until the quest becomes available.
   */
  availableAfterDays: number;
  /**
   * Number of days left until the quest ends (0 if no end or expired).
   */
  nbDaysLeft: number;
  /**
   * The computed end date of the quest, if applicable.
   */
  endOfQuest?: Date;
};

/**
 * Computes the availability of a quest based on:
 * - an absolute availability window (`absolute.start` / `absolute.end`)
 * - a relative offset after user registration (`relativeToUser.offsetDays`)
 * - an optional reference date (`eventDate`) to override the current date
 *
 * ## Parameters
 *
 * - `accessStartDate`: the date when the user first gained access to the platform.
 * - `availability`: rules that constrain when the quest can be accessed.
 * - `eventDate` *(optional)*: a reference date (typically from a `GamificationEvent`)
 *   that overrides the current date (`now`) for the computation.
 *
 * ## Rules
 *
 * 1. **No availability rules provided** → The quest is always available:
 *    - `isLocked = false`
 *    - `availableAfterDays = 0`
 *    - `nbDaysLeft = 0`
 *    - `endOfQuest = undefined`
 *
 * 2. **Absolute start date (`absolute.start`)**:
 *    - If in the **future**, the quest is locked until that date.
 *    - If in the **past or present**, the quest may be unlocked (subject to relative offset).
 *
 * 3. **Relative offset (`relativeToUser.offsetDays`)**:
 *    - The quest is locked until the user has been registered for at least `offsetDays`.
 *    - `availableAfterDays` counts remaining days until the offset is satisfied.
 *
 * 4. **Combination of absolute start and relative offset**:
 *    - The quest is **locked until both conditions are satisfied**.
 *    - Unlock occurs only when **both the absolute start date has passed and the relative offset is satisfied**.
 *
 * 5. **Absolute end date (`absolute.end`)**:
 *    - If provided, determines how many days remain before the quest ends (`nbDaysLeft`).
 *    - `endOfQuest` is the computed end date (shifted if necessary by the offset).
 *    - If no `absolute.end` is provided, the quest never expires (`nbDaysLeft = 0`).
 *
 * 6. **Event date (`eventDate`)**:
 *    - All calculations (`availableAfterDays`, `isLocked`, `nbDaysLeft`) are computed relative to this date.
 *    - If not provided, the current system date is used.
 *
 * ## Notes / Examples
 *
 * - **No availability** → quest available immediately.
 * - **Absolute start in the future** → quest locked until that date 📅.
 * - **Offset not yet reached** → quest locked until enough days have passed ⏳.
 * - **Both offset and absolute start required** → quest locked until both satisfied 🎯.
 * - **Absolute end provided** → quest has limited duration, `nbDaysLeft` and `endOfQuest` set ⏰.
 * - **With `eventDate`** → availability is computed relative to the given date instead of now 🗓️.
 */
export const computeAvailability = (
  accessStartDate: Date,
  availability?: Availability,
  eventDate?: Date,
): UseAvailabilityResult => {
  // --- No availability rules → always available ✅
  if (!availability) {
    return {
      isLocked: false,
      availableAfterDays: 0,
      nbDaysLeft: 0,
      endOfQuest: undefined,
    };
  }

  // --- Reference date (now) can be overridden by eventDate 🗓️
  const now = dayjs(eventDate);

  // --- Days since the user got access ⏳
  const daysSinceUserRegistration = now.diff(accessStartDate, 'day');

  // --- Absolute start/end dates as Dayjs objects 📅
  const absoluteStart = availability.absolute?.start ? dayjs(availability.absolute.start) : null;
  const absoluteEnd = availability.absolute?.end ? dayjs(availability.absolute.end) : null;

  // --- Relative offset (in days) after registration
  const offsetDays = availability.relativeToUser?.offsetDays ?? 0;

  // --- Helper: days remaining until a target date, optionally shifted
  const daysUntil = (target: Dayjs | null, shift = 0): number =>
    target ? target.add(shift, 'day').diff(now, 'day') : 0;

  // --- Helper: compute a shifted Date
  const shiftedDate = (target: Dayjs | null, shift = 0): Date | undefined =>
    target ? target.add(shift, 'day').toDate() : undefined;

  // --- Compute availableAfterDays
  let availableAfterDays = 0;

  if (offsetDays > 0) {
    const offsetRemaining = offsetDays - daysSinceUserRegistration;
    const absoluteRemaining = daysUntil(absoluteStart);

    if (daysSinceUserRegistration === 0) {
      // 🎯 User just got access → must wait both offset + absolute start
      availableAfterDays = offsetDays + absoluteRemaining;
    } else if (offsetRemaining > 0) {
      // ⏳ Offset not yet reached → wait remaining offset days
      availableAfterDays = offsetRemaining;
    } else {
      // ✅ Offset reached → wait until absolute start shifted by offset
      availableAfterDays = daysUntil(absoluteStart, offsetDays);
    }
  }

  // --- Locked if still before absolute start or offset not satisfied 🔒
  const isLocked = (absoluteStart && now.isBefore(absoluteStart)) || availableAfterDays > 0;

  // --- Days left until quest ends ⏰
  const nbDaysLeft = daysUntil(absoluteEnd, availableAfterDays);

  // --- Compute end of quest 🗓️
  const endOfQuest = shiftedDate(absoluteEnd, availableAfterDays);

  return {
    isLocked,
    availableAfterDays,
    nbDaysLeft,
    endOfQuest,
  };
};
